/**
 * FRONT-3269: count WHY the app reloads under a user. Reported symptom: after
 * a break, the first click is lost and the current screen reloads instead —
 * on every environment, so a deploy cannot be the only cause. Candidates:
 * the browser discarded the sleeping tab, the stale-build self-heal reloaded
 * after a deploy, or the session died. Each boot that is not a plain page open
 * is reported once to Sentry with its reason, so prod tells us which one wins.
 */

/** One-shot marker the app sets right before it navigates the page itself. */
const LOAD_REASON_MARKER = 'eddy:load-reason';
/** When the tab last went hidden; survives a discard (same tab, same session). */
const HIDDEN_AT = 'eddy:hidden-at';

/** A full page load the app started itself, recorded before it navigates. */
export type AppNavigationReason = 'stale-build' | 'session-lost';
export type LoadReason = 'discarded' | AppNavigationReason | 'reload' | 'back_forward';

function isAppNavigationReason(value: string | null): value is AppNavigationReason {
  return value === 'stale-build' || value === 'session-lost';
}

/**
 * Order matters: a discarded tab and the app's own navigations (stale-build
 * self-heal, session-lost redirect to login) all come back with a navigation
 * type that would otherwise read as a plain `reload` / `navigate`. A plain `navigate` (typing the URL, opening a link) is not a
 * reload and returns null so normal page opens never reach Sentry.
 */
export function classifyLoad(input: {
  wasDiscarded: boolean;
  appNavigation: AppNavigationReason | null;
  navigationType: string | undefined;
}): LoadReason | null {
  if (input.wasDiscarded) return 'discarded';
  if (input.appNavigation) return input.appNavigation;
  if (input.navigationType === 'reload') return 'reload';
  if (input.navigationType === 'back_forward') return 'back_forward';
  return null;
}

/** Whole minutes the tab was hidden before this boot, or null when unknown. */
export function awayMinutes(hiddenAt: number | null, now: number): number | null {
  if (hiddenAt === null || !Number.isFinite(hiddenAt) || hiddenAt > now) return null;
  return Math.floor((now - hiddenAt) / 60_000);
}

/**
 * Where the stale-build self-heal should land. During a client navigation the
 * router already knows the target (the lazy chunk for it is what 404'd);
 * loading THAT url keeps the user's click. Outside a navigation (the very
 * first boot) there is no target, so the current page reloads as before.
 */
export function staleBuildReloadTarget(pendingHref: string | null, currentHref: string): string | null {
  if (!pendingHref || pendingHref === currentHref) return null;
  return pendingHref;
}

function readStorage(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string | null): void {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    /* storage blocked — the report just loses this detail */
  }
}

/** Call right before the app itself navigates the page, so the next boot can say why. */
export function markAppNavigation(reason: AppNavigationReason): void {
  writeStorage(LOAD_REASON_MARKER, reason);
}

/**
 * Track hidden time so a report can say how long the break was. Kept in
 * sessionStorage, not memory, because a discarded tab loses memory but keeps
 * the tab's session storage.
 */
export function trackVisibility(): void {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      writeStorage(HIDDEN_AT, String(Date.now()));
      return;
    }
    writeStorage(HIDDEN_AT, null);
  });
}

/** Read and consume this boot's reason. Call once, at startup. */
export function readBootLoadReason(): { reason: LoadReason | null; awayMinutes: number | null } {
  const marker = readStorage(LOAD_REASON_MARKER);
  writeStorage(LOAD_REASON_MARKER, null);
  const hiddenAt = Number(readStorage(HIDDEN_AT) ?? NaN);
  writeStorage(HIDDEN_AT, null);
  const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  const reason = classifyLoad({
    wasDiscarded: (document as Document & { wasDiscarded?: boolean }).wasDiscarded === true,
    appNavigation: isAppNavigationReason(marker) ? marker : null,
    navigationType: nav?.type,
  });
  return { reason, awayMinutes: awayMinutes(hiddenAt, Date.now()) };
}
